/**
 * Helpers for reading the error shape the lift operator service actually returns for the tag
 * endpoints — verified against staging:
 *
 *   { status: 409, errors: [{ error: 'filter_tag_in_use', usageCount: 50 }], timestamp, cid }
 *
 * Note the plural: the payload carries an `errors` array, not a single `error` with an `extra`
 * object. Reading the wrong shape silently loses the usage count.
 */

type TagError = {
    status?: number;
    data?: {
        errors?: Array<{ error?: string; usageCount?: number }>;
    };
};

const firstError = (err: unknown) => (err as TagError)?.data?.errors?.[0];

export const isConflict = (err: unknown): boolean =>
    (err as TagError)?.status === 409;

export const errorCode = (err: unknown): string | undefined =>
    firstError(err)?.error;

/**
 * Number of product configurations still using the tag, reported when deleting without `force`.
 * Falls back to 0 so the caller can still offer the force delete on any conflict, even if the
 * payload ever stops carrying the count.
 */
export const usageCount = (err: unknown): number =>
    firstError(err)?.usageCount ?? 0;

export const saveErrorMessage = (err: unknown, subject: string): string => {
    const code = errorCode(err);
    if (
        isConflict(err) ||
        code === 'filter_tag_already_exists' ||
        code === 'product_tag_already_exists'
    ) {
        return `Es gibt bereits ${subject} mit diesem deutschen Namen.`;
    }
    if (code === 'name_de_required' || code === 'name_required') {
        return 'Bitte einen deutschen Namen angeben.';
    }
    return 'Speichern fehlgeschlagen';
};
